import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.name || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) {
      navigate('/cart');
    }
  }, [cart]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.address || !formData.city || !formData.zipCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderData = {
        email: formData.email,
        name: formData.name,
        items: cart.items,
        shipping_address: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          country: formData.country
        }
      };

      const config = user ? { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } } : {};
      const orderResponse = await axios.post(`${API}/orders`, orderData, config);
      const orderId = orderResponse.data.id;

      if (paymentMethod === 'stripe') {
        // Create Stripe checkout session
        const origin = window.location.origin;
        const checkoutResponse = await axios.post(`${API}/payments/stripe/checkout`, {
          order_id: orderId,
          origin_url: origin
        });

        // Redirect to Stripe
        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else if (paymentMethod === 'paypal') {
        // Create PayPal order
        const paypalResponse = await axios.post(`${API}/payments/paypal/create-order`, {
          order_id: orderId
        });

        // Redirect to PayPal
        if (paypalResponse.data.approval_url) {
          window.location.href = paypalResponse.data.approval_url;
        } else {
          throw new Error('No PayPal approval URL received');
        }
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to process checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8" data-testid="checkout-title">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="p-6 border-0 shadow-md mb-6">
                <h2 className="text-xl font-bold mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-0 shadow-md">
                <h2 className="text-xl font-bold mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        data-testid="state-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        data-testid="zip-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        data-testid="country-input"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-0 shadow-md">
                <h2 className="text-xl font-bold mb-6">Payment Method</h2>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-gray-50" data-testid="stripe-option">
                    <RadioGroupItem value="stripe" id="stripe" />
                    <Label htmlFor="stripe" className="flex-1 cursor-pointer flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-[#635BFF]" />
                      <div>
                        <div className="font-semibold">Credit/Debit Card</div>
                        <div className="text-sm text-gray-600">Pay securely with Stripe</div>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-gray-50" data-testid="paypal-option">
                    <RadioGroupItem value="paypal" id="paypal" />
                    <Label htmlFor="paypal" className="flex-1 cursor-pointer flex items-center gap-3">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0070BA">
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.795.68l-.04.22-.63 3.993-.028.15a.806.806 0 01-.795.68H7.72a.483.483 0 01-.477-.558L9.22 7.664a.946.946 0 01.935-.805h4.766c1.633 0 2.848.37 3.616 1.104.38.365.658.79.831 1.267a5.617 5.617 0 01.199.248z"/>
                        <path d="M7.03 8.464c.06-.328.27-.612.546-.77a1.134 1.134 0 01.532-.13h6.81c.777 0 1.452.07 2.018.214.12.03.236.06.35.095.09.03.177.06.261.091.044.018.087.035.13.054.24.103.46.226.652.369.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.795.68l-.04.22-.63 3.993-.028.15a.806.806 0 01-.795.68H7.72a.483.483 0 01-.477-.558l1.353-8.563z" fillOpacity=".7"/>
                      </svg>
                      <div>
                        <div className="font-semibold">PayPal</div>
                        <div className="text-sm text-gray-600">Pay with your PayPal account</div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </Card>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="p-6 border-0 shadow-md sticky top-24">
                <h2 className="text-xl font-bold mb-6">Order Summary</h2>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Items ({cart.items?.length || 0})</span>
                    <span data-testid="checkout-subtotal">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="text-green-600 font-semibold">FREE</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="text-[#FF6B35]" data-testid="checkout-total">${getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FF6B35] hover:bg-[#ff8c5a] text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                  data-testid="payment-button"
                >
                  {loading ? (
                    <div className="spinner mx-auto" style={{ width: '24px', height: '24px', borderWidth: '2px' }}></div>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-5 w-5" />
                      {paymentMethod === 'stripe' ? 'Pay with Stripe' : 'Pay with PayPal'}
                    </>
                  )}
                </Button>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
